/**
 * Auth state for the platform admin SPA.
 *
 * Three states:
 *   - unauthenticated   no session, show login
 *   - challenge         password verified, awaiting 2FA
 *   - authenticated     fully signed in
 */

import { create } from 'zustand';
import type { MeResponse } from '../api/client';

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated'; error: string | null }
  | { status: 'challenge'; challengeToken: string; expiresAt: number; error: string | null }
  | { status: 'authenticated'; me: MeResponse };

interface AuthStore {
  state: AuthState;
  setLoading: () => void;
  setUnauthenticated: (error?: string | null) => void;
  setChallenge: (challengeToken: string, expiresInSeconds: number) => void;
  setAuthenticated: (me: MeResponse) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
  state: { status: 'loading' },
  setLoading: () => set({ state: { status: 'loading' } }),
  setUnauthenticated: (error = null) => set({ state: { status: 'unauthenticated', error } }),
  setChallenge: (challengeToken, expiresInSeconds) =>
    set({
      state: {
        status: 'challenge',
        challengeToken,
        expiresAt: Date.now() + expiresInSeconds * 1000,
        error: null,
      },
    }),
  setAuthenticated: (me) => set({ state: { status: 'authenticated', me } }),
  clearError: () =>
    set((s) => {
      if (s.state.status === 'unauthenticated') {
        return { state: { ...s.state, error: null } };
      }
      if (s.state.status === 'challenge') {
        return { state: { ...s.state, error: null } };
      }
      return s;
    }),
}));
